import React, { useState } from "react";

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 100,
        background: checked ? "var(--accent)" : "var(--surface-2)",
        border: "none", cursor: "pointer", position: "relative",
        transition: "background 0.2s ease", flexShrink: 0,
        boxShadow: checked ? "0 0 12px rgba(59,130,246,0.4)" : "none",
      }}
    >
      <span style={{
        position: "absolute",
        top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: "white",
        transition: "left 0.2s ease",
        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
      }} />
    </button>
  );
}

function SettingRow({ label, desc, checked, onChange }) {
  return (
    <div className="setting-row">
      <div className="setting-info">
        <div className="setting-label">{label}</div>
        {desc && <div className="setting-desc">{desc}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

function SelectRow({ label, desc, value, options, onChange }) {
  return (
    <div className="setting-row">
      <div className="setting-info">
        <div className="setting-label">{label}</div>
        {desc && <div className="setting-desc">{desc}</div>}
      </div>
      <select
        className="sort-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ minWidth: 140 }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SettingsGroup({ icon, title, accent, children }) {
  return (
    <div className="page-card">
      <div className="page-card-header">
        <div className={`page-card-icon ${accent ? `page-card-icon--${accent}` : ""}`}>{icon}</div>
        {title}
      </div>
      <div className="settings-rows">{children}</div>
    </div>
  );
}

function Settings() {
  const [prefs, setPrefs] = useState({
    emailNotifs: true,
    smsAlerts: false,
    priceDropAlerts: true,
    flightStatusUpdates: true,
    darkMode: localStorage.getItem("darkMode") === "false" ? false : true,
    compactView: false,
    autoFill: true,
    saveSearch: true,
    currency: "INR",
    language: "en",
    defaultClass: "economy",
    sortDefault: "price_asc",
  });

  React.useEffect(() => {
  if (!prefs.darkMode) {
    document.body.classList.add('light');
  } else {
    document.body.classList.remove('light');
  }
}, [prefs.darkMode]);

React.useEffect(() => {
  localStorage.setItem("darkMode", prefs.darkMode);
}, [prefs.darkMode]);

  const set = (key) => (val) => setPrefs((p) => ({ ...p, [key]: val }));

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-h1">Settings</h1>
          <p className="page-desc">Manage your preferences and account configuration.</p>
        </div>
        <button className="page-empty-cta" style={{ textDecoration: "none" }}>
          Save Changes
        </button>
      </div>

      <div className="settings-grid">
        <SettingsGroup
          title="Notifications"
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>}
        >
          <SettingRow label="Email Notifications" desc="Booking confirmations and updates via email" checked={prefs.emailNotifs} onChange={set("emailNotifs")} />
          <SettingRow label="SMS Alerts" desc="Receive text messages for flight updates" checked={prefs.smsAlerts} onChange={set("smsAlerts")} />
          <SettingRow label="Price Drop Alerts" desc="Get notified when fares drop on saved routes" checked={prefs.priceDropAlerts} onChange={set("priceDropAlerts")} />
          <SettingRow label="Flight Status Updates" desc="Real-time departure and arrival notifications" checked={prefs.flightStatusUpdates} onChange={set("flightStatusUpdates")} />
        </SettingsGroup>

        <SettingsGroup
          title="Appearance"
          accent="purple"
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>}
        >
          <SettingRow label="Dark Mode" desc="Use dark theme across the dashboard" checked={prefs.darkMode} onChange={set("darkMode")} />
          <SettingRow label="Compact View" desc="Reduce spacing for more content on screen" checked={prefs.compactView} onChange={set("compactView")} />
          <SelectRow
            label="Language"
            desc="Interface display language"
            value={prefs.language}
            onChange={set("language")}
            options={[{ value: "en", label: "English" }, { value: "hi", label: "Hindi" }, { value: "te", label: "Telugu" }]}
          />
          <SelectRow
            label="Currency"
            desc="Prices displayed in this currency"
            value={prefs.currency}
            onChange={set("currency")}
            options={[{ value: "INR", label: "₹ INR" }, { value: "USD", label: "$ USD" }, { value: "EUR", label: "€ EUR" }]}
          />
        </SettingsGroup>

        <SettingsGroup
          title="Search Defaults"
          accent="green"
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
        >
          <SettingRow label="Auto-fill Passenger Details" desc="Pre-fill your name and email in forms" checked={prefs.autoFill} onChange={set("autoFill")} />
          <SettingRow label="Save Recent Searches" desc="Remember your last searched routes" checked={prefs.saveSearch} onChange={set("saveSearch")} />
          <SelectRow
            label="Default Cabin Class"
            value={prefs.defaultClass}
            onChange={set("defaultClass")}
            options={[{ value: "economy", label: "Economy" }, { value: "business", label: "Business" }, { value: "first", label: "First Class" }]}
          />
          <SelectRow
            label="Default Sort"
            value={prefs.sortDefault}
            onChange={set("sortDefault")}
            options={[
              { value: "price_asc", label: "Price: Low to High" },
              { value: "price_desc", label: "Price: High to Low" },
              { value: "duration", label: "Duration" },
              { value: "rating", label: "Best Rated" },
            ]}
          />
        </SettingsGroup>

        <SettingsGroup
          title="Account"
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
        >
          <div className="setting-row">
            <div className="setting-info">
              <div className="setting-label">Change Password</div>
              <div className="setting-desc">Update your account password</div>
            </div>
            <button className="settings-action-btn">Update →</button>
          </div>
          <div className="setting-row">
            <div className="setting-info">
              <div className="setting-label">Two-Factor Authentication</div>
              <div className="setting-desc">Add an extra layer of security to your account</div>
            </div>
            <button className="settings-action-btn">Enable →</button>
          </div>
          <div className="setting-row">
            <div className="setting-info">
              <div className="setting-label" style={{ color: "var(--red)" }}>Delete Account</div>
              <div className="setting-desc">Permanently remove your account and all data</div>
            </div>
            <button className="settings-action-btn settings-action-btn--danger">Delete</button>
          </div>
        </SettingsGroup>
      </div>
    </div>
  );
}

export default Settings;